import type React from 'react';

import clsx from 'clsx';

import type { BaseComponentProps } from '@coursera/cds-common';
import { ChevronNextIcon } from '@coursera/cds-icons';

import { useAccordionContext } from '@core/accordions/context';
import ButtonBase from '@core/ButtonBase';

import { classes } from './accordionHeaderCss';
import {
  useBlurHandler,
  useClickHandler,
  useFocusHandler,
  useKeyDownHandler,
  useMouseEnterHandler,
  useMouseLeaveHandler,
} from './events';

export type Props = BaseComponentProps<'button'> & {
  children?: React.ReactNode | React.ReactNode[];
};

const HeaderButton = (props: Props) => {
  const {
    children,
    onClick,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    ...buttonProps
  } = props;

  const context = useAccordionContext();

  return (
    <ButtonBase
      ref={context?.headerRef}
      aria-expanded={context?.expanded || false}
      classes={{
        root: classes.button,
        focusVisible: undefined /** We need to specifically override focusVisible class value in ButtonBase */,
      }}
      component="button"
      onBlur={useBlurHandler(onBlur)}
      onClick={useClickHandler(onClick)}
      onFocusVisible={useFocusHandler(onFocus)}
      onKeyDown={useKeyDownHandler(onKeyDown)}
      onMouseEnter={useMouseEnterHandler(onMouseEnter)}
      onMouseLeave={useMouseLeaveHandler(onMouseLeave)}
      {...buttonProps}
    >
      <ChevronNextIcon
        className={clsx(classes.chevron, {
          [classes.expanded]: context?.expanded,
        })}
        size="large"
      />

      <div className={classes.content}>{children}</div>
    </ButtonBase>
  );
};

export default HeaderButton;
